'use client';

import React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertCircle, CheckCircle2, Info, AlertTriangle } from 'lucide-react';

/**
 * Friendly Alert Dialog Component
 * Replaces browser alert() with a beautiful, accessible dialog
 */
export function AlertDialog({
  isOpen,
  onClose,
  title,
  message,
  type = 'info' // 'success', 'warning', 'error', 'info'
}) {
  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircle2 className="w-6 h-6 text-green-600" />;
      case 'warning':
        return <AlertTriangle className="w-6 h-6 text-yellow-600" />;
      case 'error':
        return <AlertCircle className="w-6 h-6 text-red-600" />;
      default:
        return <Info className="w-6 h-6 text-blue-600" />;
    }
  };

  const getColors = () => {
    switch (type) {
      case 'success':
        return 'bg-green-50 border-green-200';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200';
      case 'error':
        return 'bg-red-50 border-red-200';
      default:
        return 'bg-blue-50 border-blue-200';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md z-[9999]">
        <DialogHeader>
          <div className={`flex items-center gap-3 p-3 rounded-lg border ${getColors()}`}>
            {getIcon()}
            <div className="flex-1">
              <DialogTitle className="text-base font-MetSemiBold mb-1">
                {title || (type === 'error' ? 'Error' : type === 'warning' ? 'Warning' : type === 'success' ? 'Success' : 'Information')}
              </DialogTitle>
              <DialogDescription className="text-sm text-gray-700">
                {message}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <div className="flex justify-end mt-4">
          <Button
            onClick={onClose}
            className="font-MetMedium text-sm bg-blue-600 hover:bg-blue-700"
          >
            OK
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Custom hook for using AlertDialog
 * Usage:
 * const showAlert = useAlertDialog();
 * showAlert('Message here', 'warning');
 */
export function useAlertDialog() {
  const [alertState, setAlertState] = React.useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'info'
  });

  const showAlert = (message, type = 'info', title = '') => {
    setAlertState({
      isOpen: true,
      title,
      message,
      type
    });
  };

  const closeAlert = () => {
    setAlertState(prev => ({ ...prev, isOpen: false }));
  };

  const AlertDialogComponent = () => (
    <AlertDialog
      isOpen={alertState.isOpen}
      onClose={closeAlert}
      title={alertState.title}
      message={alertState.message}
      type={alertState.type}
    />
  );

  return { showAlert, closeAlert, AlertDialogComponent };
}
